// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei
// Portions derived from Excalidraw (https://github.com/excalidraw/excalidraw),
// MIT License, Copyright (c) 2020 Excalidraw — frame membership concept
// adapted (and simplified) from packages/element/src/frame.ts.

/**
 * Frames: named rectangular partitions. Membership is by containment — an
 * element belongs to a frame when its bounding-box center sits inside the
 * frame. Hosts use `frameChildren` to move a frame's contents along with it.
 * v1 recomputes membership on demand (no persisted `frameId` back-reference).
 */

import { elementBBox, rectCenter } from './geometry'
import type { BoardElement, BoardScene, FrameElement } from './types'

export function isFrame(el: BoardElement): el is FrameElement {
  return el.type === 'frame'
}

/**
 * Which frame (by id) clips each non-frame element: the innermost (smallest)
 * frame whose bbox contains the element's center. Elements outside every frame
 * are absent. Used by the renderer to clip frame content to frame bounds.
 */
export function frameClipMap(scene: BoardScene): Map<string, string> {
  const frames = scene.elements.filter((e): e is FrameElement => e.type === 'frame')
  const map = new Map<string, string>()
  if (frames.length === 0) return map
  const boxes = frames.map((f) => {
    const b = elementBBox(f)
    return { id: f.id, b, area: b.width * b.height }
  })
  for (const el of scene.elements) {
    if (el.type === 'frame') continue
    const c = rectCenter(elementBBox(el))
    let best: { id: string; area: number } | null = null
    for (const { id, b, area } of boxes) {
      if (c.x >= b.x && c.x <= b.x + b.width && c.y >= b.y && c.y <= b.y + b.height) {
        if (!best || area < best.area) best = { id, area }
      }
    }
    if (best) map.set(el.id, best.id)
  }
  return map
}

/** `<clipPath>` defs (one per frame) matching each frame's rounded rect. */
export function frameClipDefs(scene: BoardScene): string {
  const frames = scene.elements.filter((e): e is FrameElement => e.type === 'frame')
  if (frames.length === 0) return ''
  const clips = frames
    .map((f) => {
      const b = elementBBox(f)
      return `<clipPath id="mboard-fclip-${f.id}"><rect x="${b.x}" y="${b.y}" width="${b.width}" height="${b.height}" rx="6"/></clipPath>`
    })
    .join('')
  return `<defs>${clips}</defs>`
}

/** Non-frame elements whose bbox center lies inside the given frame. */
export function frameChildren(scene: BoardScene, frameId: string): BoardElement[] {
  const frame = scene.elements.find((e) => e.id === frameId && e.type === 'frame')
  if (!frame) return []
  const fb = elementBBox(frame)
  const inside = (el: BoardElement) => {
    const c = rectCenter(elementBBox(el))
    return c.x >= fb.x && c.x <= fb.x + fb.width && c.y >= fb.y && c.y <= fb.y + fb.height
  }
  return scene.elements.filter((e) => e.type !== 'frame' && e.id !== frameId && inside(e))
}
